import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import ReactDOM from 'react-dom';

// Simple Portal component to render children at the document body level
const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  const portalRoot = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create a div that we'll render the portal content into
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left = '0';
    div.style.width = '100%';
    div.style.zIndex = '99999';
    portalRoot.current = div;
    document.body.appendChild(div);
    setMounted(true);

    // Clean up function
    return () => {
      if (portalRoot.current) {
        document.body.removeChild(portalRoot.current);
      }
    };
  }, []);

  return mounted && portalRoot.current ? ReactDOM.createPortal(children, portalRoot.current) : null;
};

interface AutocompleteChipFieldProps {
  label?: string;
  placeholder: string;
  value: string[];
  options: string[];
  onChange: (values: string[]) => void;
  maxItems?: number;
  helperText?: string;
  errorMessage?: string;
}

export const AutocompleteChipField: React.FC<AutocompleteChipFieldProps> = ({
  label,
  placeholder,
  value,
  options,
  onChange,
  maxItems = 5,
  helperText,
  errorMessage
}) => {
  // Add debug log for incoming props
  console.log(`AutocompleteChipField (${label || placeholder}):`, { 
    selectedValues: value,
    selectedCount: value.length, 
    availableOptions: options.length > 0 ? options.slice(0, 3) : 'empty', // Show first few options
    maxItems
  });

  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Log component render
  useEffect(() => {
    console.log(`AutocompleteChipField (${label || placeholder}): Component rendered with ${value.length} selected values`);
    
    // If there are values, log them
    if (value.length > 0) {
      console.log(`AutocompleteChipField (${label || placeholder}): Selected values:`, value);
    }
    
    // Check if our options include all selected values
    const missingOptions = value.filter(v => !options.includes(v));
    if (missingOptions.length > 0) {
      console.warn(`AutocompleteChipField (${label || placeholder}): Some selected values are not in options list:`, missingOptions);
    }
  }, [value, options, label, placeholder]);

  // Filter options based on input value and exclude already selected items
  useEffect(() => {
    if (inputValue.trim() === '') {
      const availableOptions = options.filter(option => !value.includes(option)).slice(0, 5);
      setFilteredOptions(availableOptions);
      console.log(`AutocompleteChipField (${label || placeholder}): filtered options (empty input)`, availableOptions);
    } else {
      const matchingOptions = options
        .filter(option => 
          !value.includes(option) && 
          option.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 5);
      setFilteredOptions(matchingOptions);
      console.log(`AutocompleteChipField (${label || placeholder}): filtered options (with input "${inputValue}")`, matchingOptions);
    }
  }, [inputValue, options, value, label, placeholder]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setSelectedOptionIndex(-1);
  };

  const handleOptionSelect = (option: string) => {
    if (value.length < maxItems) {
      onChange([...value, option]);
      setInputValue('');
      setSelectedOptionIndex(-1);
    }
  };

  const handleRemoveChip = (removedOption: string) => {
    onChange(value.filter(option => option !== removedOption));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
      e.preventDefault();
      setSelectedOptionIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp' && filteredOptions.length > 0) {
      e.preventDefault();
      setSelectedOptionIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      
      if (selectedOptionIndex >= 0 && selectedOptionIndex < filteredOptions.length) {
        handleOptionSelect(filteredOptions[selectedOptionIndex]);
      } else if (inputValue.trim() !== '') {
        // If the input matches an option exactly, add it
        const exactMatch = options.find(
          option => option.toLowerCase() === inputValue.toLowerCase()
        );
        
        if (exactMatch && !value.includes(exactMatch)) {
          handleOptionSelect(exactMatch);
        } else if (filteredOptions.length > 0) {
          // Otherwise add the first filtered option
          handleOptionSelect(filteredOptions[0]);
        } else if (!options.includes(inputValue) && value.length < maxItems) {
          // Allow custom entries if not in options
          onChange([...value, inputValue]);
          setInputValue('');
        }
      }
    }
  };

  return (
    <div className="w-full mb-4">
      {label && (
        <label 
          htmlFor={`autocomplete-${label}`} 
          className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className={`
          flex flex-wrap gap-2 p-3 bg-surface-light dark:bg-surface-dark 
          border ${errorMessage ? 'border-error-light dark:border-error-dark' : 'border-border-light dark:border-border-dark'} 
          rounded-lg focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent 
          transition-all min-h-[56px]
        `}>
          {value.map(chip => (
            <div key={chip} className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-primary-light/10 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark">
              <span className="mr-1 font-medium">{chip}</span>
              <button
                type="button"
                onClick={() => handleRemoveChip(chip)}
                className="p-0.5 rounded-full hover:bg-primary-light/20 dark:hover:bg-primary-dark/30 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          
          <input
            id={`autocomplete-${label}`}
            ref={inputRef}
            type="text"
            className={`text-sm
              flex-grow min-w-[120px] bg-transparent border-none p-0 focus:outline-none
              text-text-primary-light dark:text-text-primary-dark
              placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark
            `}
            placeholder={value.length === 0 ? placeholder : value.length >= maxItems ? 'Maximum items reached' : 'Add more...'}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={value.length >= maxItems}
          />
        </div>
        
        {isFocused && filteredOptions.length > 0 && (
          <Portal>
            <div
              className="fixed"
              style={{
                position: 'absolute',
                width: `${inputRef.current?.getBoundingClientRect().width || 0}px`,
                top: `${(inputRef.current?.getBoundingClientRect().bottom || 0) + window.scrollY + 8}px`,
                left: `${(inputRef.current?.getBoundingClientRect().left || 0) - 12}px`
              }}
            >
              <div
                className="w-full rounded-md bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark shadow-lg max-h-60 overflow-auto"
                ref={dropdownRef}
              >
                <ul className="py-1">
                  {filteredOptions.map((option, index) => (
                    <li
                      key={index}
                      className={`px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${
                        index === selectedOptionIndex
                          ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                          : 'text-text-primary-light dark:text-text-primary-dark hover:bg-background-light/50 dark:hover:bg-background-dark/50'
                      }`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {option}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Portal>
        )}
      </div>
      
      {helperText && !errorMessage && (
        <p className="mt-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
          {helperText}
        </p>
      )}
      
      {errorMessage && (
        <p className="mt-1 text-xs text-error-light dark:text-error-dark">
          {errorMessage}
        </p>
      )}
    </div>
  );
}; 